'use client';

import { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Plus, Lock, AlertCircle, CheckCircle } from 'lucide-react';
import { api } from '../../core/apiClient';

interface Period {
  id: number;
  name: string;
  start_date: string;
  end_date: string;
  status: 'Abierto' | 'Cerrado';
  created_at: string;
}

interface ClosingEntry {
  id: number;
  period_id: number;
  date: string;
  description: string;
  status: 'Propuesta' | 'Aprobada' | 'Confirmada' | 'Revertida';
  created_by: string;
}

const CLOSING_STATUS_LABELS: Record<string, string> = {
  Propuesta: 'Proposta',
  Aprobada: 'Aprovada',
  Confirmada: 'Confirmada',
  Revertida: 'Revertida',
};

export default function PeriodClosing() {
  const [periods, setPeriods] = useState<Period[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<Period | null>(null);
  const [closingHistory, setClosingHistory] = useState<ClosingEntry[]>([]);
  const [showNewPeriodForm, setShowNewPeriodForm] = useState(false);
  const [closingStep, setClosingStep] = useState<'select' | 'review' | 'approve' | 'confirm' | null>(null);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    start_date: '',
    end_date: '',
  });

  useEffect(() => {
    loadPeriods();
  }, []);

  const loadPeriods = async () => {
    try {
      const response: any = await api.get('/comptabilitat/periods');
      if (response.success) {
        setPeriods(response.data || []);
      }
    } catch (error) {
      console.error('Error carregant períodes:', error);
    }
  };

  const loadClosingHistory = async (periodId: number) => {
    try {
      const response: any = await api.get(`/comptabilitat/periods/closings/${periodId}`);
      if (response.success) {
        setClosingHistory(response.data || []);
      }
    } catch (error) {
      console.error('Error carregant historial:', error);
    }
  };

  const handleCreatePeriod = async () => {
    if (!formData.name || !formData.start_date || !formData.end_date) {
      alert('Cal omplir tots els camps');
      return;
    }

    try {
      await api.post('/comptabilitat/periods', formData);
      setShowNewPeriodForm(false);
      setFormData({ name: '', start_date: '', end_date: '' });
      loadPeriods();
      alert('Període creat correctament');
    } catch (error: any) {
      alert('Error: ' + error.message);
    }
  };

  const handleSelectPeriod = async (period: Period) => {
    setSelectedPeriod(period);
    loadClosingHistory(period.id);
    setClosingStep('review');
  };

  const handleCreateClosingProposal = async () => {
    if (!selectedPeriod) return;

    setLoading(true);
    try {
      await api.post('/comptabilitat/periods/closings', {
        period_id: selectedPeriod.id,
        created_by: 'current-user',
      });
      loadClosingHistory(selectedPeriod.id);
      setClosingStep('approve');
      alert('Proposta de tancament creada');
    } catch (error: any) {
      alert('Error: ' + error.message);
    }
    setLoading(false);
  };

  const handleApproveClosing = async (closingId: number) => {
    setLoading(true);
    try {
      await api.put(`/comptabilitat/periods/closings/${closingId}/approve`);
      loadClosingHistory(selectedPeriod!.id);
      alert('Tancament aprovat');
    } catch (error: any) {
      alert('Error: ' + error.message);
    }
    setLoading(false);
  };

  const handleConfirmClosing = async (closingId: number) => {
    if (!window.confirm('Confirmes el tancament del període? Aquesta acció no es pot desfer fàcilment.')) {
      return;
    }

    setLoading(true);
    try {
      await api.put(`/comptabilitat/periods/closings/${closingId}/confirm`);
      loadPeriods();
      loadClosingHistory(selectedPeriod!.id);
      setClosingStep('confirm');
      alert('Període tancat correctament');
    } catch (error: any) {
      alert('Error: ' + error.message);
    }
    setLoading(false);
  };

  const handleRevertClosing = async (closingId: number) => {
    if (!window.confirm('Vols revertir el tancament? El període es tornarà a obrir.')) {
      return;
    }

    setLoading(true);
    try {
      await api.put(`/comptabilitat/periods/closings/${closingId}/revert`);
      loadPeriods();
      loadClosingHistory(selectedPeriod!.id);
      alert('Tancament revertit, període reobert');
    } catch (error: any) {
      alert('Error: ' + error.message);
    }
    setLoading(false);
  };

  const latestClosing = closingHistory.length > 0 ? closingHistory[0] : null;

  return (
    <div className="flex flex-col gap-6 p-6 max-w-6xl mx-auto">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Tancament de Períodes</h1>
        <Button onClick={() => setShowNewPeriodForm(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          Nou Període
        </Button>
      </div>

      {showNewPeriodForm && (
        <Card className="p-6">
          <h2 className="text-sm font-bold mb-4">Crear Nou Període</h2>
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div>
              <label className="text-sm font-medium">Nom del Període</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="2026 - 1r Trimestre"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Data Inici</label>
              <Input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Data Final</label>
              <Input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleCreatePeriod} className="bg-green-600 hover:bg-green-700">
              Crear Període
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowNewPeriodForm(false);
                setFormData({ name: '', start_date: '', end_date: '' });
              }}
            >
              Cancel·lar
            </Button>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-2 gap-6">
        <Card className="p-6">
          <h2 className="font-bold text-sm mb-4">Períodes</h2>
          <div className="space-y-2">
            {periods.map((period) => (
              <button
                key={period.id}
                onClick={() => handleSelectPeriod(period)}
                className={`w-full text-left p-3 rounded border-2 transition-colors ${
                  selectedPeriod?.id === period.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-bold">{period.name}</div>
                    <div className="text-xs text-gray-600">
                      {new Date(period.start_date).toLocaleDateString('ca-ES')} -{' '}
                      {new Date(period.end_date).toLocaleDateString('ca-ES')}
                    </div>
                  </div>
                  <span
                    className={`px-2 py-1 rounded text-xs font-bold ${
                      period.status === 'Cerrado'
                        ? 'bg-gray-200 text-gray-800'
                        : 'bg-green-200 text-green-800'
                    }`}
                  >
                    {period.status === 'Cerrado' ? 'Tancat' : 'Obert'}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </Card>

        {selectedPeriod && (
          <Card className="p-6">
            <h2 className="font-bold text-sm mb-4">Assistent de Tancament</h2>

            {selectedPeriod.status === 'Cerrado' ? (
              <div className="p-4 bg-gray-100 rounded text-center">
                <Lock className="h-12 w-12 mx-auto mb-2 text-gray-600" />
                <div className="font-bold">Període Tancat</div>
                <div className="text-sm text-gray-600 mt-1">Aquest període està bloquejat per a l&apos;edició</div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 rounded border-l-4 border-blue-500">
                  <div className="font-bold flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    Pas 1: Revisar
                  </div>
                  <div className="text-sm mt-1">
                    Verificar que tots els assentaments estiguin comptabilitzats
                  </div>
                  {closingStep !== 'select' && (
                    <div className="mt-2 text-sm text-green-700 flex items-center gap-1">
                      <CheckCircle className="h-4 w-4" />
                      Completat
                    </div>
                  )}
                </div>

                {closingStep === 'review' && (
                  <Button
                    onClick={handleCreateClosingProposal}
                    disabled={loading}
                    className="w-full bg-blue-600 hover:bg-gray-700"
                  >
                    {loading ? 'Processant...' : 'Procedir al Pas 2'}
                  </Button>
                )}

                {closingStep && closingStep !== 'select' && (
                  <>
                    <div className="p-4 bg-yellow-50 rounded border-l-4 border-yellow-500">
                      <div className="font-bold flex items-center gap-2">
                        <AlertCircle className="h-4 w-4" />
                        Pas 2: Aprovar
                      </div>
                      <div className="text-sm mt-1">
                        El responsable ha de revisar i aprovar el tancament
                      </div>
                      {latestClosing?.status === 'Propuesta' && (
                        <Button
                          onClick={() => handleApproveClosing(latestClosing.id)}
                          disabled={loading}
                          className="mt-3 w-full bg-yellow-600 hover:bg-yellow-700"
                        >
                          {loading ? 'Processant...' : 'Aprovar Tancament'}
                        </Button>
                      )}
                      {latestClosing?.status === 'Aprobada' && (
                        <div className="mt-2 text-sm text-green-700 flex items-center gap-1">
                          <CheckCircle className="h-4 w-4" />
                          Tancament Aprovat
                        </div>
                      )}
                    </div>

                    {latestClosing?.status === 'Aprobada' && (
                      <div className="p-4 bg-red-50 rounded border-l-4 border-red-500">
                        <div className="font-bold flex items-center gap-2">
                          <AlertCircle className="h-4 w-4" />
                          Pas 3: Confirmar (IRREVERSIBLE)
                        </div>
                        <div className="text-sm mt-1">
                          Un cop confirmat, el període es tancarà permanentment
                        </div>
                        <Button
                          onClick={() => handleConfirmClosing(latestClosing.id)}
                          disabled={loading}
                          className="mt-3 w-full bg-red-600 hover:bg-red-700"
                        >
                          {loading ? 'Processant...' : 'Confirmar i Tancar Període'}
                        </Button>
                      </div>
                    )}

                    {latestClosing?.status === 'Confirmada' && (
                      <div className="p-4 bg-green-50 rounded border-l-4 border-green-500">
                        <div className="font-bold text-green-700 flex items-center gap-2">
                          <CheckCircle className="h-4 w-4" />
                          Període Tancat
                        </div>
                        <Button
                          onClick={() => handleRevertClosing(latestClosing.id)}
                          disabled={loading}
                          variant="outline"
                          className="mt-3 w-full text-red-600 hover:text-red-700"
                        >
                          {loading ? 'Processant...' : 'Revertir Tancament'}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </Card>
        )}
      </div>

      {selectedPeriod && closingHistory.length > 0 && (
        <Card className="p-6">
          <h2 className="font-bold text-sm mb-4">Historial de Tancaments</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-100 border-b">
                  <th className="text-left p-3">Data</th>
                  <th className="text-left p-3">Descripció</th>
                  <th className="text-left p-3">Estat</th>
                  <th className="text-left p-3">Creat per</th>
                </tr>
              </thead>
              <tbody>
                {closingHistory.map((closing) => (
                  <tr key={closing.id} className="border-b hover:bg-gray-50">
                    <td className="p-3">
                      {new Date(closing.date).toLocaleDateString('ca-ES')}
                    </td>
                    <td className="p-3">{closing.description}</td>
                    <td className="p-3">
                      <span
                        className={`px-2 py-1 rounded text-xs font-bold ${
                          closing.status === 'Confirmada'
                            ? 'bg-green-100 text-green-800'
                            : closing.status === 'Aprobada'
                            ? 'bg-yellow-100 text-yellow-800'
                            : closing.status === 'Revertida'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}
                      >
                        {CLOSING_STATUS_LABELS[closing.status] || closing.status}
                      </span>
                    </td>
                    <td className="p-3 text-gray-600">{closing.created_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
